import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';

import { EquipmentInfoRequest } from 'api/DeviceRequest';
import DeptOption from 'components/widget/DeptOption';
import ErrorMessage from 'components/widget/ErrorMessage';
import DeviceInfo from 'model/DeviceInfo';
import { EquipmentInfo } from 'model/Equipment';
import { DEVICE_MODAL_TYPE, YN_TYPE } from 'types/enumType';
import { convertDeviceTypeToString } from 'util/commonUtil';

import { equipmentInfoSchema } from './resolver/deviceResolvers';

type EquipModalProps = {
  type: DEVICE_MODAL_TYPE;
  equipment: EquipmentInfo | DeviceInfo;
  isOpen: boolean;
  isAdmin: boolean;
  onClose: () => void;
  onSubmit: (formData: EquipmentInfoRequest) => void;
};

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    padding: 0,
    border: 0,
  },
};
export default function EquipModal({
  type,
  equipment,
  isOpen,
  isAdmin,
  onClose,
  onSubmit,
}: EquipModalProps) {
  const { t } = useTranslation();
  const CODE_LIST = ['OV', 'BF', 'OS', 'CV', 'OB / LK'];
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<EquipmentInfoRequest>({
    resolver: equipmentInfoSchema,
  });

  useEffect(() => {
    if (!isOpen || !equipment) {
      return;
    }

    reset({
      ...equipment,
      eqCd: equipment.id < 0 ? CODE_LIST[4] : equipment.eqCd,
    });
  }, [isOpen, equipment, reset]);

  return (
    <ReactModal isOpen={isOpen} style={customStyles} onRequestClose={onClose}>
      <div id={'popup-wrap'}>
        <div className={'popup-wrap device-modal'} id={'equip-modal'}>
          <div className={'popup-head'}>
            <p className={'poptit'}>{convertDeviceTypeToString(type)}</p>
          </div>
          <div className={'popup-cont'}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className={'cont'}>
                <div className={'filter-wrap'}>
                  <div className={'filter'}>
                    {equipment.id > 0 && (
                      <dl>
                        <dt>{t('device.qrCode')}</dt>
                        <dd>
                          <input type={'text'} {...register('code')} disabled />
                        </dd>
                      </dl>
                    )}
                    <dl>
                      <dt>{t('device.equipCode')}</dt>
                      <dd>
                        <input
                          type={'text'}
                          defaultValue={equipment.name}
                          {...register('itemCode')}
                          disabled={!isAdmin}
                        />
                      </dd>
                    </dl>
                    <dl>
                      <dt>{`${convertDeviceTypeToString(type)} ${t(
                        'user.name',
                      )}`}</dt>
                      <dd>
                        <input
                          type={'text'}
                          defaultValue={equipment.name}
                          {...register('name')}
                          disabled={!isAdmin}
                        />
                        <input
                          type={'hidden'}
                          defaultValue={equipment.id}
                          {...register('id')}
                          disabled={!isAdmin}
                        />
                        <input
                          type={'hidden'}
                          defaultValue={type}
                          {...register('type')}
                          disabled={!isAdmin}
                        />
                      </dd>
                    </dl>
                    <dl>
                      <dt>{t('device.code')}</dt>
                      <dd>
                        <select {...register('eqCd')} disabled={!isAdmin}>
                          {CODE_LIST.map((code) => (
                            <option key={code} value={code}>
                              {code}
                            </option>
                          ))}
                        </select>
                      </dd>
                    </dl>
                    <dl>
                      <dt>{t('user.deptName')}</dt>
                      <dd>
                        <select {...register('deptId')}>
                          <DeptOption />
                        </select>
                      </dd>
                    </dl>
                    <dl>
                      <dt>{t('device.mdmCode')}</dt>
                      <dd>
                        <input
                          type={'text'}
                          defaultValue={equipment.name}
                          {...register('mdmCode')}
                          disabled={!isAdmin}
                        />
                      </dd>
                    </dl>
                    {type === DEVICE_MODAL_TYPE.DEVICE && equipment.id > 0 && (
                      <dl>
                        <dt>{t('device.useStatus')}</dt>
                        <dd>
                          <ul className={'td-radio'}>
                            <li>
                              <input
                                type={'radio'}
                                id={'use'}
                                value={YN_TYPE.Y}
                                {...register('activate')}
                                disabled={!isAdmin}
                              />
                              <label htmlFor={'use'}>{t('device.use')}</label>
                            </li>
                            <li>
                              <input
                                type={'radio'}
                                id={'notUse'}
                                value={YN_TYPE.N}
                                {...register('activate')}
                                disabled={!isAdmin}
                              />
                              <label htmlFor={'notUse'}>
                                {t('device.notUsed')}
                              </label>
                            </li>
                          </ul>
                        </dd>
                      </dl>
                    )}
                  </div>
                </div>
                <ErrorMessage errors={errors} />

                <div className={'btn-wrap'}>
                  {isAdmin && (
                    <button type={'submit'} className={'orange_white_btn'}>
                      {t('button.save')}
                    </button>
                  )}
                  <button
                    type={'button'}
                    className={'gray_white_btn'}
                    onClick={onClose}
                  >
                    {t('button.close')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </ReactModal>
  );
}
